import React from 'react';
import { motion } from 'framer-motion';

const steps = [
  {
    num: '1.',
    title: 'Understand first.',
    desc: "We start by going through each topic carefully, breaking down complex ideas so you really understand what we're learning. You're not memorising, you're building intuition — the kind that pays off in every exam question.",
    image: '/images/understand-first.jpeg',
  },
  {
    num: '2.',
    title: 'Exam Ready.',
    desc: 'Then we layer in exam technique — mark schemes, past paper drills, time management, and the small habits that turn understanding into top grades.',
    image: '/images/exam-ready.jpeg',
  },
  {
    num: '3.',
    title: 'Built around YOU.',
    desc: "Every plan is shaped around your pace, your goals, and the topics that need the most love. No two students are the same — your tutoring shouldn't be either.",
    image: '/images/built-around-you.jpeg',
  },
];

const MethodStep = ({ step, index, reverse }) => {
  return (
    <div className={`grid md:grid-cols-2 gap-10 md:gap-16 items-center ${reverse ? 'md:[&>*:first-child]:order-2' : ''}`}>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="font-display text-[20px] text-neutral-400 mb-3">{step.num}</div>
        <h3 className="font-display text-[30px] md:text-[40px] tracking-tight font-semibold text-neutral-900">
          {step.title}
        </h3>
        <p className="mt-5 text-[15px] md:text-[16px] leading-[1.75] text-neutral-700 max-w-md">
          {step.desc}
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="w-full max-w-[480px] mx-auto"
      >
        <img src={step.image} alt={step.title} className="block w-full h-auto rounded-3xl" />
      </motion.div>
    </div>
  );
};

const TheMethod = () => {
  return (
    <section id="the-method" className="py-20 md:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="font-display text-[40px] md:text-[56px] tracking-tight font-semibold text-neutral-900"
          >
            The method.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-4 text-[15px] text-neutral-600"
          >
            A clear three-step process designed to take you from confusion to confidence.
          </motion.p>
        </div>

        <div className="mt-16 md:mt-24 space-y-20 md:space-y-28">
          {steps.map((step, i) => (
            <MethodStep key={i} step={step} index={i} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TheMethod;
